import { Controller } from '@hotwired/stimulus';
import $ from 'jquery';
import Rails from '@rails/ujs';

export default class extends Controller {
  static targets = ['counties', 'organizations', 'venues'];

  connect() {
    this.bindSelect2();
    // Limit collections up to 300. In reality, no state in USA has more than
    // ~250 counties. If user are selecting more than 300 of any of the collections,
    // we asume they selected all of them.
    this.collectionLimit = 300;
    this.addAllOption();
  }

  bindSelect2() {
    if (this.hasCountiesTarget) this.bindSelect2ForCounties();
    if (this.hasOrganizationsTarget) this.bindSelect2ForOrganizations();
    if (this.hasVenuesTarget) this.bindSelect2ForVenues();
  }

  bindSelect2ForCounties() {
    $(this.countiesTarget).select2({
      width: '100%',
      ajax: {
        url: '/search/counties',
        dataType: 'json',
        type: 'POST',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
        data: (params) => ({
          q: {
            name_cont: params.term,
          },
          page: params.page || 1,
        }),
        processResults: (data, params) => {
          // eslint-disable-next-line no-param-reassign
          params.page = data.page || 1;
          return {
            results: $.map(data.data, (county) => ({
              text: county.attributes.name,
              id: county.attributes.id,
              disabled: $(this.countiesTarget).find('option[value="all"]').length > 0,
            })),
            pagination: {
              more: data.links[0]?.next,
            },
          };
        },
        cache: false,
      },
    });

    this.bindSelect2Change($(this.countiesTarget));
    this.bindSelect2Unselect($(this.countiesTarget));
    this.bindSelect2OnSelect($(this.countiesTarget));
  }

  bindSelect2ForOrganizations() {
    const that = this;
    $(this.organizationsTarget).select2({
      width: '100%',
      ajax: {
        url: '/search/organizations',
        type: 'POST',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
        dataType: 'json',
        data: (params) => ({
          q: {
            name_cont: params.term,
            county_id_in: $(this.countiesTarget).val(),
          },
          page: params.page || 1,
        }),
        processResults: (data, params) => {
          // eslint-disable-next-line no-param-reassign
          params.page = data.page || 1;
          return {
            results: $.map(data.data, (organization) => ({
              text: organization.attributes.name,
              id: organization.attributes.id,
              disabled: $(this.organizationsTarget).find('option[value="all"]').length > 0,
            })),
            pagination: {
              more: data.links[0]?.next,
            },
          };
        },
        cache: false,
      },
    }).on('select2:opening', (e) => {
      if (that.countiesTarget.value === null || that.countiesTarget.value === '') {
        e.preventDefault();
      }
    });

    this.bindSelect2Change($(this.organizationsTarget));
    this.bindSelect2Unselect($(this.organizationsTarget));
    this.bindSelect2OnSelect($(this.organizationsTarget));
  }

  bindSelect2ForVenues() {
    const that = this;
    const allOptionPresent = $(this.venuesTarget).find('option[value="all"]').length > 0;

    $(this.venuesTarget).select2({
      width: '100%',
      ajax: {
        url: '/search/venues',
        type: 'POST',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
        dataType: 'json',
        data: (params) => ({
          q: {
            name_cont: params.term,
            organization_id_in: $(this.organizationsTarget).val(),
          },
          page: params.page || 1,
        }),
        processResults: (data, params) => {
          // eslint-disable-next-line no-param-reassign
          params.page = data.page || 1;
          return {
            results: $.map(data.data, (venue) => ({
              text: venue.attributes.name,
              id: venue.attributes.id,
              disabled: allOptionPresent,
            })),
            pagination: {
              more: data.links[0]?.next,
            },
          };
        },
        cache: false,
      },
    }).on('select2:opening', (e) => {
      if (that.organizationsTarget.value === null || that.organizationsTarget.value === '') {
        e.preventDefault();
      }
    });

    this.bindSelect2Change($(this.venuesTarget));
    this.bindSelect2Unselect($(this.venuesTarget));
    this.bindSelect2OnSelect($(this.venuesTarget));
  }

  selectAllCounties() {
    $(this.countiesTarget).empty();

    Rails.ajax({
      type: 'POST',
      url: '/search/counties',
      data: `limit=${this.collectionLimit}`,
      dataType: 'json',
      success: (data) => {
        const options = data.data.map((county) => (
          new Option(county.attributes.name, county.attributes.id, true, true)
        ));

        const allOption = new Option('All', 'all', true, true);
        $(this.countiesTarget).append(options);
        if (options.length >= this.collectionLimit) $(this.countiesTarget).append(allOption);
        $(this.countiesTarget).trigger('change');
      },
    });
  }

  selectAllOrganizations() {
    $(this.organizationsTarget).empty();

    const countyIds = $(this.countiesTarget).val();
    const countyIdsQueryString = countyIds.map((id) => `q[county_id_in][]=${id}`).join('&');
    const queryString = `limit=${this.collectionLimit}&${countyIdsQueryString}`;

    Rails.ajax({
      type: 'POST',
      url: '/search/organizations',
      data: queryString,
      dataType: 'json',
      success: (data) => {
        const options = data.data.map((organization) => (
          new Option(organization.attributes.name, organization.attributes.id, true, true)
        ));

        const allOption = new Option('All', 'all', true, true);
        $(this.organizationsTarget).append(options);
        if (options.length >= this.collectionLimit) $(this.organizationsTarget).append(allOption);
        $(this.organizationsTarget).trigger('change');
      },
    });
  }

  selectAllVenues() {
    $(this.venuesTarget).empty();
    const organizationIds = $(this.organizationsTarget).val();
    const countyIds = $(this.countiesTarget).val();
    const countyIdsQueryString = countyIds.map((id) => `q[county_id_in][]=${id}`).join('&');
    const organizationIdsQueryString = organizationIds.map((id) => `q[organization_id_in][]=${id}`).join('&');
    const queryString = `limit=${this.collectionLimit}&${countyIdsQueryString}&${organizationIdsQueryString}`;

    Rails.ajax({
      type: 'POST',
      url: '/search/venues',
      data: queryString,
      dataType: 'json',
      success: (data) => {
        const options = data.data.map((venue) => (
          new Option(venue.attributes.name, venue.attributes.id, true, true)
        ));

        const allOption = new Option('All', 'all', true, true);
        $(this.venuesTarget).append(options);
        if (options.length >= this.collectionLimit) $(this.venuesTarget).append(allOption);
        $(this.venuesTarget).trigger('change');
      },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  bindSelect2Change(element) {
    element.on('select2:select', (e) => {
      const event = new Event('change', { bubbles: true });
      e.target.dispatchEvent(event);
    });
  }

  bindSelect2Unselect(element) {
    element.on('select2:unselect', (e) => {
      const event = new Event('change', { bubbles: true });
      e.target.dispatchEvent(event);

      // Manually remove the option from the original select field. For some reason
      // select2 does not remove the option from the original select field and this
      // is causing issues with the logic.
      const unselectedOption = e.params.data;
      const originalSelect = element[0];
      const optionToRemove = Array.from(originalSelect.options).find(
        (option) => option.value === unselectedOption.id,
      );
      if (optionToRemove) {
        originalSelect.remove(optionToRemove.index);
      }

      // There's a limit, but the All option is extra. That's why we check with + 1
      if (element.val().length <= this.collectionLimit + 1) {
        element.find('option[value="all"]').remove();
      }

      // Add it back in case the removed one was the All option and the collection is
      // still within the limit (300)
      if (optionToRemove.value === 'all' && element.val().length <= this.collectionLimit) {
        element.append(new Option('All', 'all', true, true)).trigger('change');
      }
    });
  }

  addAllOption() {
    if ($(this.countiesTarget).val().length >= this.collectionLimit) {
      const allOption = new Option('All', 'all', true, true);
      $(this.countiesTarget).append(allOption).trigger('change');
    }

    if ($(this.organizationsTarget).val().length >= this.collectionLimit) {
      const allOption = new Option('All', 'all', true, true);
      $(this.organizationsTarget).append(allOption).trigger('change');
    }

    if ($(this.venuesTarget).val().length >= this.collectionLimit) {
      const allOption = new Option('All', 'all', true, true);
      $(this.venuesTarget).append(allOption).trigger('change');
    }
  }

  bindSelect2OnSelect(element) {
    element.on('select2:select', () => {
      if (element.val().length > this.collectionLimit) {
        const allOption = new Option('All', 'all', true, true);
        element.append(allOption).trigger('change');
      }
    });
  }
}
